import { Capacitor } from '@capacitor/core';

import { useModalStore } from '@/stores/modal';
import { addAppStateChangeListener, removeAppStateChangeListener } from '@/utils/native/nativeBridge';
import { brightnessSet, brightnessGet, keepAwake, allowSleep } from '@/utils/native/nativeBridge';

const isIdleLocked = ref(false);
let isInitialized = false;

export const useIdleLock = () => {
  const modalStore = useModalStore();
  const router = useRouter();

  const isMobile = Capacitor.isNativePlatform();

  const msToWait = 60_000; // 자동 화면 잠김 시간이 주로 30초이므로, 1초 여유를 두고 설정
  const brightnessIdle = 0.1;
  const brightnessActive = 0.5;
  let timer: number | undefined;
  let intervalDimnessHold: number | undefined;
  let brightnessOriginal: number = 0.8;
  let yStart: number | null = null;

  const onAppStateChange = async (isActive: boolean) => {
    if (!isActive) {
      await disUseIdleLock();
      await exitIdleLock();
      router.back();
      return;
    }
    resetTimer();
  };

  // 손가락 닿았을 떄
  const onTouchStart = (e: TouchEvent) => {
    yStart = e.touches?.[0]?.clientY ?? null;

    resetTimer();
  };

  // 손가락 뗐을 때
  const onTouchEnd = (e: TouchEvent) => {
    if (!isIdleLocked.value || yStart === null) return;

    const yEnd = e.changedTouches?.[0]?.clientY ?? yStart;
    const yDelta = yEnd - yStart;
    yStart = null;

    if (yDelta < -40) {
      exitIdleLock();
      resetTimer();
    }
  };

  // 절전모드 진입 타이머 초기화
  const resetTimer = () => {
    if (timer) {
      window.clearTimeout(timer);
      timer = undefined;
    }

    if (isIdleLocked.value || !isInitialized) {
      return;
    }

    timer = window.setTimeout(() => {
      enterIdleLock();
    }, msToWait);
  };

  // 절전모드 진입
  const enterIdleLock = async () => {
    if (!isInitialized || isIdleLocked.value) return;

    await updateBrightnessNormal();
    await brightnessSet({ brightness: brightnessIdle });
    intervalDimnessHold = window.setInterval(async () => {
      await brightnessSet({ brightness: brightnessIdle });
    }, 1_000);
    isIdleLocked.value = true;
    modalStore.openModal({ modalKey: 'MobileOverlayIdleLock' });
  };

  // 절전모드 해제
  const exitIdleLock = async () => {
    const brightnessToBe = Math.max(brightnessOriginal, brightnessActive);
    await brightnessSet({ brightness: brightnessToBe });
    isIdleLocked.value = false;
    modalStore.closeModal();
    if (intervalDimnessHold) {
      clearInterval(intervalDimnessHold);
      intervalDimnessHold = undefined;
    }
    resetTimer();
  };

  // 기존 화면 밝기 조회/갱신
  const updateBrightnessNormal = async () => {
    brightnessOriginal = (await brightnessGet()).brightness;
  };

  const initializeIdleLock = async () => {
    if (!isMobile) return;
    if (isInitialized) return;

    try {
      isInitialized = true; // listener 중복 등록 방지
      resetTimer();

      await keepAwake();
      await updateBrightnessNormal();

      await addAppStateChangeListener(onAppStateChange);

      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
    } catch {
      isInitialized = false;
      console.error('절전모드 기능 초기화에 실패했습니다.');
    }
  };

  // 절전모드 기능 해제
  const disUseIdleLock = async () => {
    if (!isMobile) return;
    if (!isInitialized) return;

    try {
      isInitialized = false;

      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);

      if (timer) {
        window.clearTimeout(timer);
        timer = undefined;
      }

      if (intervalDimnessHold) {
        window.clearInterval(intervalDimnessHold);
        intervalDimnessHold = undefined;
      }

      yStart = null;
      removeAppStateChangeListener();
      await allowSleep();
    } catch {
      console.error('절전모드 기능 제거에 실패했습니다.');
    }
  };

  return {
    isIdleLocked: readonly(isIdleLocked),
    initializeIdleLock,
    disUseIdleLock,
  };
};
